import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { User, AuthState } from '../types';
import { authService } from '../services/auth.service';
import { clearAuthStorage, onAuthCleared } from '../services/auth-session';

interface AuthActions {
  login: (user: User, token: string) => void;
  logout: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  initializeFromStorage: () => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
}

interface AuthStore extends AuthState {
  isLoading: boolean;
  hasHydrated: boolean;
  actions: AuthActions;
}

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set) => ({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: true,
        hasHydrated: false,

        actions: {
          login: (user: User, token: string) => {
            set(
              {
                user,
                token,
                isAuthenticated: true,
                isLoading: false,
              },
              false,
              'auth/login'
            );
          },

          logout: async () => {
            try {
              await authService.logout();
            } finally {
              clearAuthStorage();
              set(
                {
                  user: null,
                  token: null,
                  isAuthenticated: false,
                  isLoading: false,
                },
                false,
                'auth/logout'
              );
            }
          },

          updateUser: (userData: Partial<User>) => {
            set(
              (state) => ({
                user: state.user ? { ...state.user, ...userData } : null,
              }),
              false,
              'auth/updateUser'
            );
          },

          clearAuth: () => {
            clearAuthStorage();
            set(
              {
                user: null,
                token: null,
                isAuthenticated: false,
                isLoading: false,
              },
              false,
              'auth/clear'
            );
          },

          initializeFromStorage: () => {
            const user = authService.getPersistedUser();
            const token = authService.getPersistedToken();

            // Keep zustand and token keys in sync — mismatched state causes
            // /login ↔ dashboard redirect flicker.
            if (user && token) {
              set(
                {
                  user,
                  token,
                  isAuthenticated: true,
                  isLoading: false,
                  hasHydrated: true,
                },
                false,
                'auth/initFromStorage'
              );
            } else {
              set(
                {
                  user: null,
                  token: null,
                  isAuthenticated: false,
                  isLoading: false,
                  hasHydrated: true,
                },
                false,
                'auth/initFromStorageEmpty'
              );
            }
          },

          setLoading: (loading: boolean) => {
            set({ isLoading: loading }, false, 'auth/setLoading');
          },
        },
      }),
      {
        name: 'field-force-auth',
        partialize: (state) => ({
          user: state.user,
          token: state.token,
          isAuthenticated: state.isAuthenticated,
        }),
        onRehydrateStorage: () => (state) => {
          const token = localStorage.getItem('access_token');
          const valid = !!(state && state.user && state.isAuthenticated && token);

          queueMicrotask(() => {
            if (!valid) {
              useAuthStore.setState({
                user: null,
                token: null,
                isAuthenticated: false,
                hasHydrated: true,
                isLoading: false,
              });
              return;
            }

            useAuthStore.setState({
              hasHydrated: true,
              isLoading: false,
            });
          });
        },
      }
    ),
    { name: 'AuthStore' }
  )
);

// Keep in-memory store cleared when API interceptor invalidates the session.
onAuthCleared(() => {
  useAuthStore.setState({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: false,
  });
});
